import * as React from 'react';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelGroupHandle,
  type ImperativePanelHandle,
  type PanelGroupProps,
  type PanelProps,
  type PanelResizeHandleProps,
} from 'react-resizable-panels';

import { cn } from '@/lib/utils';

// Canonical shadcn Resizable primitive — vendored + restyled for the
// neumorphic design language, manual-vendor convention like the other
// components in `ui/`. Wraps `react-resizable-panels`, PINNED to `3.0.6` in
// package.json — the last release with the classic
// `PanelGroup` / `Panel` / `PanelResizeHandle` API and `autoSaveId`
// localStorage persistence, which is what this file (and the shadcn
// `resizable.tsx` it's modeled on) is written against. A bare
// `npm install react-resizable-panels` resolves to `4.x`, an incompatible
// rewrite (`Group` / `Panel` / `Separator`, no `autoSaveId` — persistence
// there is a manual `useDefaultLayout` hook instead). Do not bump past `3.x`
// without rewriting this file against the new API.
//
// Used by `Layout.tsx` to make the three console columns (account sidebar /
// conversation list / chat panel) drag-resizable with per-browser layout
// persistence (`autoSaveId` writes each group's panel sizes to
// `localStorage` on every layout change and restores them on mount — no
// app-side persistence code needed).

/**
 * `ResizablePanelGroup` — the flex container that owns a set of `Resizable
 * Panel`s and the `ResizableHandle`s between them. `direction` is required
 * by the underlying primitive (`"horizontal"` | `"vertical"`) — the console
 * shell uses `"horizontal"` for its three side-by-side columns.
 */
const ResizablePanelGroup = React.forwardRef<ImperativePanelGroupHandle, PanelGroupProps>(
  ({ className, ...props }, ref) => (
    <PanelGroup
      ref={ref}
      className={cn(
        'flex h-full w-full data-[panel-group-direction=vertical]:flex-col',
        className,
      )}
      {...props}
    />
  ),
);
ResizablePanelGroup.displayName = 'ResizablePanelGroup';

/**
 * `ResizablePanel` — one resizable column. A thin pass-through of the
 * primitive's `Panel` (it already renders a `h-full` flex-column div, which
 * is what lets the mounted column component's own `h-full min-h-0` chain
 * keep working unmodified — see each column's root element).
 */
const ResizablePanel = React.forwardRef<ImperativePanelHandle, PanelProps>((props, ref) => (
  <Panel ref={ref} {...props} />
));
ResizablePanel.displayName = 'ResizablePanel';

interface ResizableHandleProps extends PanelResizeHandleProps {
  /**
   * Renders a small centered grip pill on top of the groove (the classic
   * shadcn look). Off by default here — the design calls for a bare thin
   * groove that only reveals itself on hover/drag, not a permanently
   * visible pill.
   */
  withHandle?: boolean;
}

/**
 * `ResizableHandle` — the ~6px drag handle between two panels.
 *
 * Neumorphic treatment: transparent at rest so it doesn't compete with the
 * panel's own `shadow-neu-*` edges, then reveals a soft inset "groove" via
 * the existing `shadow-neu-inset-sm` token on hover and the stronger
 * `shadow-neu-inset` on drag. Both tokens read the `--neu-inset-*` CSS vars,
 * which the `.dark` block in `index.css` already fades to near-invisible
 * (`rgba(..., 0.04-0.35)`) — so the handle automatically stays subtle in
 * dark mode with no extra dark-mode-specific classes here.
 *
 * The primitive renders a `role="separator"` div with its own focus +
 * arrow-key resize handling (`data-panel-resize-handle-enabled`) — this
 * wrapper only adds classes / an optional visual grip, it does not touch
 * that behavior, so keyboard accessibility is preserved as-is.
 */
const ResizableHandle = ({
  withHandle = false,
  className,
  ...props
}: ResizableHandleProps): React.JSX.Element => (
  <PanelResizeHandle
    className={cn(
      'group relative flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-transparent outline-none transition-colors duration-150',
      // Wider invisible hit area than the visible 6px groove — easier to
      // grab without widening the visible seam between panels.
      'after:absolute after:inset-y-0 after:left-1/2 after:w-4 after:-translate-x-1/2 after:content-[""]',
      'data-[panel-group-direction=vertical]:h-1.5 data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:cursor-row-resize',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
      className,
    )}
    {...props}
  >
    {/* The groove — invisible at rest, soft inset shadow on hover/drag. */}
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none h-10 w-1.5 rounded-full transition-shadow duration-150',
        'group-data-[resize-handle-state=hover]:shadow-neu-inset-sm',
        'group-data-[resize-handle-state=drag]:shadow-neu-inset',
        'group-data-[panel-group-direction=vertical]:h-1.5 group-data-[panel-group-direction=vertical]:w-10',
      )}
    />
    {withHandle && (
      <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm bg-[var(--raised-bg)] shadow-neu-raised-sm">
        <div className="h-2 w-0.5 rounded-full bg-neu-faint" />
      </div>
    )}
  </PanelResizeHandle>
);
ResizableHandle.displayName = 'ResizableHandle';

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
// Re-exported so consumers (e.g. `Layout.tsx`, which drives the collapsible
// account panel imperatively) get the handle type through this wrapper — the
// single boundary against the pinned `react-resizable-panels` API — rather
// than importing straight from the library.
export type { ImperativePanelHandle };
